import { Flex, Select, SelectProps, Text } from "@mantine/core";
import { forwardRef, ReactNode, useState } from "react";

import { AutoCompleteInputContainer } from "@/components/atom/inputContainer/autoCompleteInputContainer";
import { InspectionsGetRequestInspectionTypeEnum } from "@sizlcorp/sizl-api-document/dist/models";
import { IconX } from "@tabler/icons-react";
import {
  InspectionSpecificationOption,
  useInspectionSpecificationsAutoComplete,
  useInspectSpecificationsCode,
} from "./auto-useGetInspectSpecificationQuery";

interface InspectSpecificationProps
  extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string | undefined;
}

type InspectionSpecificationOnchange = { id: number };

export interface InspectSpecificationAutoCompleteProps
  extends Partial<SelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (
    equipmentCode: string | null,
    option?: InspectionSpecificationOnchange | undefined
  ) => void;
  maxDropdownHeight?: number;
  width?: string;
  itemCode?: string;
  routingCode?: string;
  inspectionType: InspectionsGetRequestInspectionTypeEnum;
}

// 입력값: value (equipment 모델의 code)
// 출력값: onChange (equipment 모델의 code)

export const InspectSpecificationAutoComplete = (
  params: InspectSpecificationAutoCompleteProps
) => {
  const {
    value: equipmentCode,
    onChange,
    maxDropdownHeight,
    width,
    itemCode,
    routingCode,
    inspectionType,
    ...etcParams
  } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = useInspectionSpecificationsAutoComplete(
    focused,
    searchKeyword,
    itemCode,
    routingCode,
    inspectionType
  );

  const { data: initialOptions } = useInspectSpecificationsCode(
    !!equipmentCode,
    equipmentCode ?? null
  );

  let selectedInspectSpecification = initialOptions?.find(
    (InspectSpecification) => InspectSpecification.value === equipmentCode
  );
  // 검색 옵션 + 초기 옵션 병합 및 중복 제거
  const mergedOptions: InspectionSpecificationOption[] = [
    ...(options ?? []),
    ...(initialOptions ?? []),
  ]
    .filter((option) => typeof option.value === "string")
    .reduce((unique: InspectionSpecificationOption[], option) => {
      return unique.some((u) => u.value === option.value)
        ? unique
        : [...unique, option];
    }, [])
    .map((option) => ({
      ...option,
      value: option.value as string,
    }));

  const onChangeHandler = (e: string | null) => {
    const found = mergedOptions.find((spec) => spec?.value === e);
    selectedInspectSpecification = found ?? selectedInspectSpecification;
    onChange && onChange(e, found ? { id: found.id } : undefined);
  };

  const SelectInspectSpecification = forwardRef<
    HTMLDivElement,
    InspectSpecificationProps
  >(({ label: name, value: code, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Flex direction="row" justify="space-between" align="center">
        <Flex align={"center"}>
          <Text>{name}</Text>
          <Text fz="xs">(설비코드: {code})</Text>
        </Flex>
      </Flex>
    </div>
  ));

  return (
    <Select
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      inputContainer={(children: ReactNode) => (
        <AutoCompleteInputContainer selectedValue={equipmentCode ?? ""}>
          {children}
        </AutoCompleteInputContainer>
      )}
      value={equipmentCode}
      itemComponent={SelectInspectSpecification}
      searchValue={searchKeyword}
      data={mergedOptions}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={(val) => onChangeHandler(val)}
      onSearchChange={setSearchKeyword}
      rightSection={InspectSpecificationInfo({
        equipment:
          selectedInspectSpecification as InspectionSpecificationOption,
        onChange: (specificationCode: string | null) =>
          onChangeHandler(specificationCode),
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      {...etcParams}
    />
  );
};

const InspectSpecificationInfo = (params: {
  equipment?: InspectionSpecificationOption;
  onChange: (specificationCode: string | null) => void;
}) => {
  const { equipment, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return equipment?.value ? (
    <IconX size="1.5rem" onClick={clearHandler} />
  ) : null;
};
